import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, Container, Typography, Card, Stack, Avatar, Grid, 
  Button, MenuItem, TextField, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Backdrop, CircularProgress,
  Alert, Snackbar, IconButton, Tooltip, Divider,
  FormControl, InputLabel, Select, SelectChangeEvent
} from '@mui/material';
import { 
  CleaningServices as CleanIcon, 
  AssignmentInd as AssignmentIndIcon, // Rename it here
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Build as BuildIcon,
  Block as BlockIcon,
  Engineering as EngineeringIcon,
  BusinessCenter as BusinessCenterIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types based on actual API responses
interface Room {
  id: number;
  product_id: string;
  name: string;
  item_status?:  'dirty' | 'out_of_order' | 'clean' | 'maintenance' | 'management';
  assigned_to?: string;
  assigned_housekeeper_name?: string;
  qty: number;
  price: string;
  totalQtyPosted?: number;
  is_assigned?: boolean;
  assignment_status?: string | null;
  room_status?: string; // Custom status for room condition
}

interface Housekeeper {
  user_id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  roleID: number;
}

// Room status options
const ROOM_STATUS_OPTIONS = [
  { value: 'clean', label: 'Clean', color: '#10b981', icon: <CheckCircleOutlineIcon /> },
  { value: 'dirty', label: 'Dirty', color: '#f59e0b', icon: <DeleteSweepIcon /> },
  { value: 'out_of_order', label: 'Out of Order', color: '#ef4444', icon: <BlockIcon /> },
  { value: 'maintenance', label: 'Maintenance', color: '#f97316', icon: <BuildIcon /> },
  { value: 'management', label: 'Management', color: '#8b5cf6', icon: <BusinessCenterIcon /> },
//   { value: 'Available', label: 'Available', color: '#10b981', icon: <CheckCircleIcon /> },
//   { value: 'Assigned', label: 'Assigned', color: '#8b5cf6', icon: <AssignmentIndIcon /> }
];

const HousekeepingDashboard: React.FC = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [housekeepers, setHousekeepers] = useState<Housekeeper[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [selectedHK, setSelectedHK] = useState<string>('');
  const [openAssign, setOpenAssign] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  
  // Snackbar states for notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const roomRes = await axios.post(`${baseUrl()}Housekeepers/getRoomsByBranch`, formData, config);
      const hkRes = await axios.post(`${baseUrl()}Housekeepers/getHousekeepers`, formData, config);

      console.log('Rooms response:', roomRes.data);
      console.log('Housekeepers response:', hkRes.data);

      if (roomRes.data.status === "true" || roomRes.data.status === true) {
        // Map the room data to include a status field using the new API fields
        const mappedRooms = roomRes.data.data.map((room: any) => ({
          ...room,
          status: determineRoomStatus(room),
          room_status: room.room_status || determineRoomStatus(room)
        }));
        setRooms(mappedRooms);
      }
      
      if (hkRes.data.status) {
        setHousekeepers(hkRes.data.data);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showNotification('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to show notifications
  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Helper function to extract room number from name
  const extractRoomNumber = (roomName: string): string => {
    const match = roomName.match(/ROOM\s+(\d+)/i);
    return match ? match[1] : roomName;
  };

  // Determine room status based on API fields
  const determineRoomStatus = (room: any): string => {
    // First check if room has a custom status
    if (room.room_status) {
      return room.room_status;
    }
    
    // Check if room is assigned using the new fields
    if (room.is_assigned === true || room.assignment_status === 'assigned') {
      return 'Assigned';
    }
    
    // Check if assigned_housekeeper_name exists (backward compatibility)
    if (room.assigned_housekeeper_name) {
      return 'Assigned';
    }
    
    // Check if out of stock
    if (room.qty === 0) {
      return 'Sold';
    }
    
    // Check if it has been posted (might indicate it needs cleaning)
    if (room.totalQtyPosted && room.totalQtyPosted > 0) {
      return 'Dirty';
    }
    
    // Default to Available
    return 'Available';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAssignSubmit = async () => {
    if (!selectedRoom || !selectedHK) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("roomId", selectedRoom.product_id);
      formData.append("housekeeperId", selectedHK);

      const res = await axios.post(`${baseUrl()}Housekeepers/create`, formData, config);
      
      if (res.data.status) {
        showNotification('Room assigned successfully!', 'success');
        setOpenAssign(false);
        setSelectedHK('');
        fetchData();
      } else {
        showNotification('Failed to assign room', 'error');
      }
    } catch (error) {
      console.error("Assignment error:", error);
      showNotification('Error assigning room', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedRoom || !selectedStatus) return;
    
    console.log(selectedStatus);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("roomId", selectedRoom.product_id);
      formData.append("status", selectedStatus);

      // Update this URL to your actual status update endpoint
      const res = await axios.post(`${baseUrl()}Housekeepers/updateRoomStatus`, formData, config);
      
      if (res.data.status) {
        showNotification(`Room status updated to ${selectedStatus}`, 'success');
        setOpenStatusDialog(false);
        setSelectedStatus('');
        fetchData();
      } else {
        showNotification('Failed to update room status', 'error');
      }
    } catch (error) {
      console.error("Status update error:", error);
      showNotification('Error updating room status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusOption = ROOM_STATUS_OPTIONS.find(opt => opt.value === status);
    return statusOption?.color || '#64748b';
  };

  const getStatusIcon = (status: string) => {
    const statusOption = ROOM_STATUS_OPTIONS.find(opt => opt.value === status);
    return statusOption?.icon || <CheckCircleIcon />;
  };

  // Format housekeeper name for display
  const getHousekeeperDisplayName = (hk: Housekeeper): string => {
    return hk.fullName || `${hk.firstName} ${hk.lastName}`.trim() || hk.email;
  };

  // Check if room can be assigned (allow reassignment even if assigned)
  const canAssignRoom = (room: Room): boolean => {
    // Allow assignment for all rooms except Sold and Out of Order
    return room.status !== 'Out of Order' && room.status !== 'Sold';
  };

  // Get assigned housekeeper name for display
  const getAssignedHousekeeperName = (room: Room): string => {
    if (room.assigned_housekeeper_name) {
      return room.assigned_housekeeper_name;
    }
    return 'Not Assigned';
  };

  const handleOpenStatusDialog = (room: Room) => {
    setSelectedRoom(room);
    setSelectedStatus(room.room_status || room.status || 'Available');
    setOpenStatusDialog(true);
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ backgroundColor: '#f5f7fb', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          
          {/* HEADER SECTION */}
          <Box sx={{ background: 'linear-gradient(to right, #1e3a8a, #2563eb)', px: 3, py: 4, color: 'white' }}>
            <Container maxWidth="xl">
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight={700}>Housekeeping</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Manage room status and staff assignments</Typography>
                </Box>
                <Button 
                  variant="contained" 
                  startIcon={<RefreshIcon />} 
                  onClick={fetchData}
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, textTransform: 'none' }}
                >
                  Refresh
                </Button>
              </Stack>
            </Container>
          </Box>

          {/* MAIN GRID */}
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {rooms.length === 0 && !loading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">No rooms available</Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {rooms.map((room) => {
                  const isAssigned = room.is_assigned === true || 
                                    room.assignment_status === 'assigned' || 
                                    room.status === 'Assigned';
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={room.id || room.product_id}>
                      <Card sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
                        border: '1px solid #e2e8f0',
                        position: 'relative',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                        }
                      }}>
                        {/* Status Badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            display: 'flex',
                            gap: 0.5,
                            zIndex: 1
                          }}
                        >
                          {isAssigned && (
                            <Box
                              sx={{
                                bgcolor: '#8b5cf6',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <AssignmentIndIcon fontSize="small" />
                              Assigned
                            </Box>
                          )}
                          
                          {/* Edit Status Button */}
                          <Tooltip title="Change Status">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenStatusDialog(room)}
                              sx={{
                                bgcolor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': {
                                  bgcolor: '#f5f5f5'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box>
                            <Typography variant="h6" fontWeight={700}>
                              {extractRoomNumber(room.name)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 200 }} noWrap>
                              {room.name}
                            </Typography>
                            <Chip 
                              icon={getStatusIcon(room.item_status || 'Available')}
                              label={ room.item_status || 'Available'} 
                              size="small" 
                              sx={{ 
                                bgcolor: getStatusColor(room.room_status || room.status || 'Available'), 
                                color: 'white', 
                                fontWeight: 600, 
                                mt: 0.5,
                                '& .MuiChip-icon': {
                                  color: 'white'
                                }
                              }} 
                            />
                          </Box>
                          <Avatar sx={{ bgcolor: '#eff6ff' }}>
                            <CleanIcon sx={{ color: '#2563eb' }} />
                          </Avatar>
                        </Stack>

                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block">Assigned To:</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {getAssignedHousekeeperName(room)} 
                          </Typography>
                          {isAssigned && room.assignment_status && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Assignment: {room.assignment_status}
                            </Typography>
                          )}
                        </Box>

                        <Stack direction="row" spacing={1}>
                          <Button 
                            fullWidth 
                            variant="outlined" 
                            startIcon={<AssignmentIndIcon />}
                            onClick={() => { setSelectedRoom(room); setOpenAssign(true); }}
                            disabled={!canAssignRoom(room)}
                            sx={{ 
                              textTransform: 'none', 
                              borderRadius: 2,
                              ...(isAssigned && {
                                borderColor: '#8b5cf6',
                                color: '#8b5cf6',
                                '&:hover': {
                                  borderColor: '#7c3aed',
                                  bgcolor: 'rgba(139, 92, 246, 0.04)'
                                }
                              })
                            }}
                          >
                            {isAssigned ? 'Reassign' : 'Assign'}
                          </Button>
                          
                          <Tooltip title="Change Status">
                            <Button
                              variant="outlined"
                              onClick={() => handleOpenStatusDialog(room)}
                              sx={{ 
                                minWidth: '40px',
                                width: '40px',
                                p: 0,
                                borderRadius: 2,
                                borderColor: '#e2e8f0'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                        </Stack>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Container>

          {/* ASSIGNMENT MODAL */}
          <Dialog open={openAssign} onClose={() => setOpenAssign(false)} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>
              {selectedRoom?.is_assigned ? 'Reassign Staff' : 'Staff Assignment'}
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Select a housekeeper for <strong>Room {selectedRoom ? extractRoomNumber(selectedRoom.name) : ''}</strong>
              </Typography>
              <TextField
                select
                fullWidth
                label="Available Housekeepers"
                value={selectedHK}
                onChange={(e) => setSelectedHK(e.target.value)}
              >
                {housekeepers.map((hk) => (
                  <MenuItem key={hk.user_id} value={hk.user_id}>
                    {getHousekeeperDisplayName(hk)}
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={() => setOpenAssign(false)} color="inherit">Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleAssignSubmit}
                disabled={!selectedHK}
                sx={{
                  bgcolor: '#2563eb',
                  '&:hover': {
                    bgcolor: '#1d4ed8'
                  }
                }}
              >
                {selectedRoom?.is_assigned ? 'Reassign' : 'Confirm Assignment'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* STATUS UPDATE MODAL */}
          <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>Update Room Status</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Change status for <strong>Room {selectedRoom ? extractRoomNumber(selectedRoom.name) : ''}</strong>
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>Room Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e: SelectChangeEvent) => setSelectedStatus(e.target.value)}
                  label="Room Status"
                >
                  {ROOM_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: option.color }}>{option.icon}</Box>
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={() => setOpenStatusDialog(false)} color="inherit">Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleStatusUpdate}
                disabled={!selectedStatus || selectedStatus === (selectedRoom?.room_status || selectedRoom?.status)}
                sx={{
                  bgcolor: '#2563eb',
                  '&:hover': {
                    bgcolor: '#1d4ed8'
                  }
                }}
              >
                Update Status
              </Button>
            </DialogActions>
          </Dialog>

          {/* SUCCESS/ERROR NOTIFICATION SNACKBAR */}
          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={6000} 
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity} 
              sx={{ width: '100%', fontWeight: 500 }}
              elevation={6}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* LOADING OVERLAY */}
          <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>

        </Box>
      </IonContent>
    </IonPage>
  );
};

export default HousekeepingDashboard;